/**
 * PurchaseService — atomic writer for "Goods Received" purchases.
 *
 * A single call to `createReceivedPurchase` performs one database.write()
 * that:
 *   1. Creates a PurchaseOrder row with status='RECEIVED'
 *   2. Creates one PoLineItem per line (received_qty == quantity)
 *   3. Updates each Product.currentStock and purchasePrice
 *      (moving-average, mirrors mobile useReceivePurchaseOrder)
 *   4. Emits a StockMovement row (type='PURCHASE') per line
 *   5. Increments Supplier.currentBalance and writes a SupplierLedger row
 *
 * For simple POS use this is the path most retailers want — they record
 * stock arriving from a supplier in one atomic step rather than the
 * two-stage PO-then-receive workflow.
 */

import { database, txn } from '@/db';
import { Q } from '@nozbe/watermelondb';
import { logActivity } from '@/utils/auditLog';
import { computePurchaseTax } from './purchaseTax';
import { loadStoreGstIdentity } from '@/gst/storeIdentity';
import { resolveInterState } from '@/utils/placeOfSupply';
import {
  PurchaseOrder,
  PoLineItem,
  Product,
  StockMovement,
  Supplier,
  SupplierPayment,
  PurchaseReturn,
  PurchaseReturnItem,
  SupplierLedger,
} from '@/db/models/Inventory';

export interface PurchaseLine {
  product: Product;
  quantity: number;
  unitPrice: number;
  taxRate?: number;
}

export async function recordSupplierPayment(input: {
  supplier: Supplier;
  storeId: string;
  amount: number;
  method: string;
  reference?: string;
  notes?: string;
}): Promise<{ ok: boolean; message?: string }> {
  if (!(input.amount > 0)) return { ok: false, message: 'Enter a valid payment amount.' };
  try {
    await txn(async (db) => {
      const nextBalance = Math.max(0, input.supplier.currentBalance - input.amount);
      const payment = await db.get<SupplierPayment>('supplier_payments').create((record) => {
        record.storeId = input.storeId;
        record.supplierId = input.supplier.id;
        record.amount = input.amount;
        record.method = input.method;
        record.transactionRef = input.reference?.trim() || null;
        record.paymentDate = new Date();
        record.notes = input.notes?.trim() || null;
        record.version = 1;
        record.isSynced = false;
        record.isDeleted = false;
      });
      await input.supplier.update((record) => {
        record.currentBalance = nextBalance;
        record.version = input.supplier.version + 1;
        record.isSynced = false;
      });
      await db.get<SupplierLedger>('supplier_ledger').create((record) => {
        record.merchantId = input.supplier.merchantId;
        record.supplierId = input.supplier.id;
        record.transactionDate = new Date();
        record.type = 'PAYMENT';
        record.amount = -input.amount;
        record.balanceAfter = nextBalance;
        record.referenceId = payment.id;
        record.referenceType = 'SUPPLIER_PAYMENT';
        record.notes = input.notes?.trim() || 'Supplier payment';
        record.version = 1;
        record.isSynced = false;
        record.isDeleted = false;
      });
    });
    void logActivity({
      action: 'supplier_payment',
      entityType: 'supplier',
      entityId: input.supplier.id,
      storeId: input.storeId,
      changes: {
        summary: `${input.supplier.name} · paid ${input.amount} via ${input.method}`,
        amount: -input.amount,
        ref: input.reference?.trim() || null,
      },
    });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : 'Failed to record payment.' };
  }
}

export async function createPurchaseReturn(input: {
  supplier: Supplier;
  storeId: string;
  createdById: string;
  reason?: string;
  notes?: string;
  lines: PurchaseLine[];
}): Promise<{ ok: boolean; returnNumber?: string; message?: string }> {
  const lines = input.lines.filter((line) => line.quantity > 0 && line.unitPrice >= 0);
  if (lines.length === 0) return { ok: false, message: 'Add at least one return line.' };
  const returnNumber = `PR-${Date.now().toString(36).toUpperCase()}`;
  const totalAmount = lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
  try {
    await txn(async (db) => {
      const ret = await db.get<PurchaseReturn>('purchase_returns').create((record) => {
        record.storeId = input.storeId;
        record.supplierId = input.supplier.id;
        record.purchaseOrderId = null;
        record.returnNumber = returnNumber;
        record.status = 'COMPLETED';
        record.reason = input.reason?.trim() || null;
        record.totalAmount = totalAmount;
        record.notes = input.notes?.trim() || null;
        record.version = 1;
        record.isSynced = false;
        record.isDeleted = false;
      });
      for (const line of lines) {
        await db.get<PurchaseReturnItem>('purchase_return_items').create((record) => {
          record.purchaseReturnId = ret.id;
          record.productId = line.product.id;
          record.batchId = null;
          record.quantity = line.quantity;
          record.unitPrice = line.unitPrice;
          record.reason = input.reason?.trim() || null;
          record.version = 1;
          record.isSynced = false;
          record.isDeleted = false;
        });
        await line.product.update((record) => {
          record.currentStock = Math.max(0, line.product.currentStock - line.quantity);
          record.version = line.product.version + 1;
          record.isSynced = false;
        });
        await db.get<StockMovement>('stock_movements').create((record) => {
          record.productId = line.product.id;
          record.godownId = null;
          record.type = 'PURCHASE_RETURN';
          record.quantity = -line.quantity;
          record.referenceId = ret.id;
          record.referenceType = 'PURCHASE_RETURN';
          record.notes = input.reason?.trim() || `Purchase return ${returnNumber}`;
          record.source = 'DESKTOP';
          record.userId = input.createdById;
          record.version = 1;
          record.isSynced = false;
          record.isDeleted = false;
        });
      }
      const nextBalance = Math.max(0, input.supplier.currentBalance - totalAmount);
      await input.supplier.update((record) => {
        record.currentBalance = nextBalance;
        record.version = input.supplier.version + 1;
        record.isSynced = false;
      });
      await db.get<SupplierLedger>('supplier_ledger').create((record) => {
        record.merchantId = input.supplier.merchantId;
        record.supplierId = input.supplier.id;
        record.transactionDate = new Date();
        record.type = 'RETURN';
        record.amount = -totalAmount;
        record.balanceAfter = nextBalance;
        record.referenceId = ret.id;
        record.referenceType = 'PURCHASE_RETURN';
        record.notes = `Purchase return ${returnNumber}`;
        record.version = 1;
        record.isSynced = false;
        record.isDeleted = false;
      });
    });
    void logActivity({
      action: 'purchase_return_created',
      entityType: 'purchase_return',
      entityId: returnNumber,
      storeId: input.storeId,
      userId: input.createdById,
      changes: {
        summary: `${returnNumber} · ${input.supplier.name} · ${lines.length} line${lines.length === 1 ? '' : 's'} · ${totalAmount}`,
        amount: -totalAmount,
        quantity: -lines.reduce((sum, l) => sum + l.quantity, 0),
        ref: returnNumber,
      },
    });
    return { ok: true, returnNumber };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : 'Failed to create return.' };
  }
}

export interface CreatePurchaseInput {
  supplier: Supplier;
  storeId: string;
  godownId?: string | null;
  supplierInvoiceNo?: string | null;
  supplierInvoiceDate?: Date | null;
  notes?: string | null;
  lines: PurchaseLine[];
  createdById: string;
}

export interface CreatePurchaseResult {
  success: boolean;
  purchaseOrderId?: string;
  poNumber?: string;
  totalAmount?: number;
  error?: string;
}

function newPoNumber(): string {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `PO-${ts}-${rand}`;
}

/** Moving-average price; mirrors mobile InventoryService.calculateMovingAveragePrice. */
function movingAvg(currentStock: number, currentPrice: number, addQty: number, addPrice: number): number {
  const totalQty = currentStock + addQty;
  if (totalQty <= 0) return addPrice;
  const totalValue = currentStock * currentPrice + addQty * addPrice;
  return totalValue / totalQty;
}

/**
 * Is this supplier out of state? Never throws: if the store's own GST identity
 * cannot be read, a purchase must still be recordable — we fall back to
 * intra-state rather than blocking the write.
 */
async function resolveSupplierInterState(
  storeId: string,
  supplierGstin: string | null,
): Promise<boolean> {
  let homeStateCode: string | null = null;
  try {
    ({ homeStateCode } = await loadStoreGstIdentity(storeId));
  } catch {
    homeStateCode = null;
  }
  return resolveInterState({ gstin: supplierGstin }, homeStateCode) ?? false;
}

export async function createReceivedPurchase(
  input: CreatePurchaseInput,
): Promise<CreatePurchaseResult> {
  const cleanLines = input.lines.filter((l) => l.quantity > 0 && l.unitPrice >= 0);
  if (cleanLines.length === 0) {
    return { success: false, error: 'Add at least one line with quantity > 0.' };
  }

  const totalAmount = cleanLines.reduce((sum, l) => {
    const lineTotal = l.quantity * l.unitPrice;
    const tax = lineTotal * ((l.taxRate ?? 0) / 100);
    return sum + lineTotal + tax;
  }, 0);

  // The tax head on an inward supply depends on where the SUPPLIER is, not on
  // which screen recorded it. A registered supplier's GSTIN carries its state
  // code in its first two digits. Supplier has no `state` column and its
  // `address` is free text, so an unregistered supplier is undeterminable: we
  // default to intra-state, which is the common case for a local shop and what
  // the draft-PO path always assumed. (The direct-receive path used to default
  // to IGST, which was wrong for exactly those suppliers.)
  const isInterState = await resolveSupplierInterState(input.storeId, input.supplier.gstin);

  const poNumber = newPoNumber();
  let purchaseOrderId = '';

  try {
    await txn(async (db) => {
      const ops: any[] = [];

      const po = db.get<PurchaseOrder>('purchase_orders').prepareCreate((row: PurchaseOrder) => {
        row.storeId = input.storeId;
        row.supplierId = input.supplier.id;
        row.poNumber = poNumber;
        row.status = 'RECEIVED';
        row.totalAmount = totalAmount;
        row.notes = input.notes ?? null;
        row.expectedDate = null;
        row.supplierInvoiceNo = input.supplierInvoiceNo ?? null;
        row.supplierInvoiceDate = input.supplierInvoiceDate ?? null;
        row.isSynced = false;
        row.version = 1;
        row.isDeleted = false;
      });
      purchaseOrderId = (po as PurchaseOrder).id;
      ops.push(po);

      // Aggregate by product (in case the user added the same SKU twice)
      const aggregated = new Map<string, { product: Product; qty: number; lastPrice: number; taxRate: number }>();
      for (const l of cleanLines) {
        const key = l.product.id;
        const prev = aggregated.get(key);
        if (prev) {
          prev.qty += l.quantity;
          prev.lastPrice = l.unitPrice; // last write wins for unit price
        } else {
          aggregated.set(key, {
            product: l.product,
            qty: l.quantity,
            lastPrice: l.unitPrice,
            taxRate: l.taxRate ?? 0,
          });
        }
      }

      // Per-line records (we keep the original line granularity for PoLineItem)
      for (const l of cleanLines) {
        const lineTotal = l.quantity * l.unitPrice;
        const taxRate = l.taxRate ?? 0;
        const tax = computePurchaseTax(lineTotal, taxRate, isInterState);
        ops.push(
          db.get<PoLineItem>('po_line_items').prepareCreate((line: PoLineItem) => {
            line.purchaseOrderId = purchaseOrderId;
            line.productId = l.product.id;
            line.quantity = l.quantity;
            line.unitPrice = l.unitPrice;
            line.receivedQty = l.quantity;
            line.taxRate = taxRate;
            line.cgstAmount = tax.cgstAmount;
            line.sgstAmount = tax.sgstAmount;
            line.igstAmount = tax.igstAmount;
            line.isSynced = false;
            line.version = 1;
            line.isDeleted = false;
          }),
        );

        ops.push(
          db.get<StockMovement>('stock_movements').prepareCreate((sm: StockMovement) => {
            sm.productId = l.product.id;
            sm.godownId = input.godownId ?? null;
            sm.type = 'PURCHASE';
            sm.quantity = l.quantity;
            sm.referenceId = purchaseOrderId;
            sm.referenceType = 'PURCHASE_ORDER';
            sm.notes = `Purchase ${poNumber}`;
            sm.source = 'DESKTOP';
            sm.userId = input.createdById;
            sm.isSynced = false;
            sm.version = 1;
            sm.isDeleted = false;
          }),
        );
      }

      // Apply aggregated product updates (currentStock + moving-avg purchase price)
      for (const { product, qty, lastPrice } of aggregated.values()) {
        const newAvg = movingAvg(product.currentStock, product.purchasePrice, qty, lastPrice);
        ops.push(
          product.prepareUpdate((p: Product) => {
            p.currentStock += qty;
            p.purchasePrice = newAvg;
            p.isSynced = false;
            p.version += 1;
          }),
        );
      }

      // Supplier balance + ledger
      const newBalance = input.supplier.currentBalance + totalAmount;
      ops.push(
        input.supplier.prepareUpdate((s: Supplier) => {
          s.currentBalance = newBalance;
          s.isSynced = false;
          s.version += 1;
        }),
      );
      ops.push(
        db.get<SupplierLedger>('supplier_ledger').prepareCreate((sl: SupplierLedger) => {
          sl.merchantId = input.supplier.merchantId;
          sl.supplierId = input.supplier.id;
          sl.transactionDate = new Date();
          sl.type = 'INVOICE';
          sl.amount = totalAmount;
          sl.balanceAfter = newBalance;
          sl.referenceId = purchaseOrderId;
          sl.referenceType = 'PURCHASE_ORDER';
          sl.notes = `Received items from PO: ${poNumber}`;
          sl.isSynced = false;
          sl.version = 1;
          sl.isDeleted = false;
        }),
      );

      await db.batch(...ops);
    });

    void logActivity({
      action: 'purchase_received',
      entityType: 'purchase_order',
      entityId: purchaseOrderId,
      storeId: input.storeId,
      userId: input.createdById,
      changes: {
        summary: `${poNumber} · ${input.supplier.name} · ${cleanLines.length} line${cleanLines.length === 1 ? '' : 's'} · ${totalAmount.toFixed(2)}`,
        amount: totalAmount,
        quantity: cleanLines.reduce((sum, l) => sum + l.quantity, 0),
        ref: poNumber,
      },
    });

    return { success: true, purchaseOrderId, poNumber, totalAmount };
  } catch (err) {
    return { success: false, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  TWO-STAGE PO WORKFLOW
// ─────────────────────────────────────────────────────────────────────────────

export interface CreateDraftPOResult {
  success: boolean;
  purchaseOrderId?: string;
  poNumber?: string;
  error?: string;
}

/**
 * createDraftPO — Stage 1: issue a formal PO to a supplier.
 *
 * Creates a PurchaseOrder with status='ORDERED' and PoLineItem rows where
 * received_qty=0.  No stock changes occur yet.
 */
export async function createDraftPO(
  input: CreatePurchaseInput & { expectedDate?: Date | null },
): Promise<CreateDraftPOResult> {
  const cleanLines = input.lines.filter((l) => l.quantity > 0 && l.unitPrice >= 0);
  if (cleanLines.length === 0) {
    return { success: false, error: 'Add at least one line with quantity > 0.' };
  }
  const totalAmount = cleanLines.reduce((sum, l) => {
    const lineTotal = l.quantity * l.unitPrice;
    const tax = lineTotal * ((l.taxRate ?? 0) / 100);
    return sum + lineTotal + tax;
  }, 0);

  // The tax head on an inward supply depends on where the SUPPLIER is, not on
  // which screen recorded it. A registered supplier's GSTIN carries its state
  // code in its first two digits. Supplier has no `state` column and its
  // `address` is free text, so an unregistered supplier is undeterminable: we
  // default to intra-state, which is the common case for a local shop and what
  // the draft-PO path always assumed. (The direct-receive path used to default
  // to IGST, which was wrong for exactly those suppliers.)
  const isInterState = await resolveSupplierInterState(input.storeId, input.supplier.gstin);

  const poNumber = newPoNumber();
  let purchaseOrderId = '';
  try {
    await txn(async (db) => {
      const po = await db.get<PurchaseOrder>('purchase_orders').create((row) => {
        row.storeId = input.storeId;
        row.supplierId = input.supplier.id;
        row.poNumber = poNumber;
        row.status = 'ORDERED';
        row.totalAmount = totalAmount;
        row.notes = input.notes ?? null;
        row.expectedDate = input.expectedDate ?? null;
        row.supplierInvoiceNo = null;
        row.supplierInvoiceDate = null;
        row.isSynced = false;
        row.version = 1;
        row.isDeleted = false;
      });
      purchaseOrderId = po.id;
      for (const l of cleanLines) {
        const lineTotal = l.quantity * l.unitPrice;
        const taxRate = l.taxRate ?? 0;
        const tax = computePurchaseTax(lineTotal, taxRate, isInterState);
        await db.get<PoLineItem>('po_line_items').create((row) => {
          row.purchaseOrderId = po.id;
          row.productId = l.product.id;
          row.quantity = l.quantity;
          row.unitPrice = l.unitPrice;
          row.receivedQty = 0;
          row.taxRate = taxRate;
          row.cgstAmount = tax.cgstAmount;
          row.sgstAmount = tax.sgstAmount;
          row.igstAmount = tax.igstAmount;
          row.isSynced = false;
          row.version = 1;
          row.isDeleted = false;
        });
      }
    });
    void logActivity({
      action: 'po_created',
      entityType: 'purchase_order',
      entityId: purchaseOrderId,
      storeId: input.storeId,
      userId: input.createdById,
      changes: {
        summary: `${poNumber} ordered · ${input.supplier.name} · ${cleanLines.length} line${cleanLines.length === 1 ? '' : 's'} · ${totalAmount.toFixed(2)}`,
        amount: totalAmount,
        ref: poNumber,
      },
    });
    return { success: true, purchaseOrderId, poNumber };
  } catch (err) {
    return { success: false, error: (err as Error).message };
  }
}

export interface ReceivePOLine {
  lineItem: PoLineItem;
  product: Product;
  receivedQty: number;
}

export interface ReceivePOInput {
  purchaseOrder: PurchaseOrder;
  storeId: string;
  createdById: string;
  lines: ReceivePOLine[];
  supplierInvoiceNo?: string | null;
  supplierInvoiceDate?: Date | null;
  notes?: string | null;
}

export interface ReceivePOResult {
  success: boolean;
  newStatus?: string;
  error?: string;
}

/**
 * receiveAgainstPO — Stage 2: record goods arriving against an existing PO.
 *
 * - Updates received_qty for each PoLineItem (additive — supports partial receipts).
 * - Sets PO status = 'PARTIAL' if any line is still under-received,
 *   or 'RECEIVED' once all ordered quantities have been received.
 * - Increments stock (moving-average price) and emits StockMovement rows.
 * - Updates supplier balance + SupplierLedger for the amount received.
 */
export async function receiveAgainstPO(input: ReceivePOInput): Promise<ReceivePOResult> {
  const toReceive = input.lines.filter((l) => l.receivedQty > 0);
  if (toReceive.length === 0) {
    return { success: false, error: 'Enter at least one quantity to receive.' };
  }
  if (input.purchaseOrder.status === 'RECEIVED') {
    return { success: false, error: 'This purchase order is already fully received.' };
  }
  const invalidQty = input.lines.find((l) => !Number.isFinite(l.receivedQty) || l.receivedQty < 0);
  if (invalidQty) {
    return { success: false, error: 'Received quantity must be a valid non-negative number.' };
  }
  const overReceive = toReceive.find((l) => l.receivedQty > l.lineItem.quantity - l.lineItem.receivedQty);
  if (overReceive) {
    return { success: false, error: 'Cannot receive more than the ordered quantity.' };
  }
  try {
    let newStatus = 'PARTIAL';
    let receivedValue = 0;
    const receivedQty = toReceive.reduce((sum, l) => sum + l.receivedQty, 0);
    await txn(async (db) => {
      // 1. Update each PoLineItem's received_qty.
      const updatedLines: PoLineItem[] = [];
      for (const l of toReceive) {
        const newReceived = l.lineItem.receivedQty + l.receivedQty;
        await l.lineItem.update((row) => {
          row.receivedQty = newReceived;
          row.version = l.lineItem.version + 1;
          row.isSynced = false;
        });
        updatedLines.push(l.lineItem);
      }
      // 2. Determine overall PO status.
      //    Reload all line items to compare ordered vs received.
      const allLines = await database
        .get<PoLineItem>('po_line_items')
        .query(Q.where('purchase_order_id', input.purchaseOrder.id), Q.where('is_deleted', false))
        .fetch();
      const allReceived = allLines.every((li) => li.receivedQty >= li.quantity);
      newStatus = allReceived ? 'RECEIVED' : 'PARTIAL';
      await input.purchaseOrder.update((row) => {
        row.status = newStatus;
        row.supplierInvoiceNo = input.supplierInvoiceNo ?? row.supplierInvoiceNo;
        row.supplierInvoiceDate = input.supplierInvoiceDate ?? row.supplierInvoiceDate;
        row.notes = input.notes ?? row.notes;
        row.version = input.purchaseOrder.version + 1;
        row.isSynced = false;
      });
      // 3. Update stock via moving-average and emit StockMovements.
      const receivedTotal = toReceive.reduce(
        (sum, l) => sum + l.receivedQty * l.lineItem.unitPrice * (1 + l.lineItem.taxRate / 100),
        0,
      );
      receivedValue = receivedTotal;
      for (const l of toReceive) {
        const newAvgPrice = movingAvg(
          l.product.currentStock,
          l.product.purchasePrice ?? l.lineItem.unitPrice,
          l.receivedQty,
          l.lineItem.unitPrice,
        );
        await l.product.update((row) => {
          row.currentStock = l.product.currentStock + l.receivedQty;
          row.purchasePrice = newAvgPrice;
          row.version = l.product.version + 1;
          row.isSynced = false;
        });
        await db.get<StockMovement>('stock_movements').create((row) => {
          row.productId = l.product.id;
          row.godownId = null;
          row.type = 'PURCHASE';
          row.quantity = l.receivedQty;
          row.referenceId = input.purchaseOrder.id;
          row.referenceType = 'PURCHASE_ORDER';
          row.notes = `Received against ${input.purchaseOrder.poNumber}`;
          row.source = 'DESKTOP';
          row.userId = input.createdById;
          row.version = 1;
          row.isSynced = false;
          row.isDeleted = false;
        });
      }
      // 4. Increment supplier balance and write ledger entry.
      const supplier = await input.purchaseOrder.supplier.fetch() as Supplier;
      const nextBalance = supplier.currentBalance + receivedTotal;
      await supplier.update((row) => {
        row.currentBalance = nextBalance;
        row.version = supplier.version + 1;
        row.isSynced = false;
      });
      await db.get<SupplierLedger>('supplier_ledger').create((row) => {
        row.merchantId = supplier.merchantId;
        row.supplierId = supplier.id;
        row.transactionDate = new Date();
        row.type = 'PURCHASE';
        row.amount = receivedTotal;
        row.balanceAfter = nextBalance;
        row.referenceId = input.purchaseOrder.id;
        row.referenceType = 'PURCHASE_ORDER';
        row.notes = `Goods received against ${input.purchaseOrder.poNumber}`;
        row.version = 1;
        row.isSynced = false;
        row.isDeleted = false;
      });
    });
    void logActivity({
      action: 'po_received',
      entityType: 'purchase_order',
      entityId: input.purchaseOrder.id,
      storeId: input.storeId,
      userId: input.createdById,
      changes: {
        summary: `${input.purchaseOrder.poNumber} · ${receivedQty} units received · ${newStatus}`,
        amount: receivedValue,
        quantity: receivedQty,
        ref: input.purchaseOrder.poNumber,
        after: { status: newStatus },
      },
    });
    return { success: true, newStatus };
  } catch (err) {
    return { success: false, error: (err as Error).message };
  }
}
